import React, { MouseEvent, useCallback, useEffect } from 'react';
import { Box, ClickAwayListener } from '@mui/material';
import SwipeableDrawer from '@core/dashboard/components/SwipeableDrawer';
import HomeBarButton from '../HomeBarButton';

import { useToggle } from './hooks';
import { IIconPopoverView } from './interfaces';
import { useMytaverseEvent } from '@core/dashboard/providers';
import { AnalyticsType } from '@interfaces/analytics';
import { useOverlayContext } from '@providers/OverlayProvider';
import { useTeleportModal } from '@core/dashboard/components/TeleportModal/context/context';

const IconPopover = ({
  children,
  icon,
  label,
  onClick = null,
  buttonSx = {},
  popoverSx = {},
  position = {
    vertical: 'bottom',
    horizontal: 'right',
  },
  disableClickAway = false,
  analyticsName,
  overlayName,
  disabled = false,
  dataTestId,
}: IIconPopoverView) => {
  const { trackEventAnalytics, setIsTeleportingToRoomByUnreal } =
    useMytaverseEvent();
  const { isOpen, handleClick, handleClose, handleOpen } = useToggle();
  const {
    addToCurrentlyOpenOverlays,
    removeFromCurrentlyOpenOverlays,
    isOverlayCurrentlyOpen,
    hideCurrentlyOpenOverlays,
  } = useOverlayContext();
  const { open: teleportModalOpen, setOpen: setTeleportModalOpen } =
    useTeleportModal();

  const handleToggleDrawer = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      if (!isOpen) {
        // if opening — close teleport modal if it's open
        if (teleportModalOpen) {
          setTeleportModalOpen(false);
          setIsTeleportingToRoomByUnreal(false);
        }
        addToCurrentlyOpenOverlays(overlayName);
      } else {
        // if closing
        removeFromCurrentlyOpenOverlays(overlayName);
      }

      handleClick(e);
      onClick?.(!isOpen);
      if (!isOpen && analyticsName) {
        trackEventAnalytics(AnalyticsType.NavbarButtonClick, {
          name: analyticsName,
        });
      }
    },
    [
      handleClick,
      isOpen,
      onClick,
      analyticsName,
      trackEventAnalytics,
      overlayName,
      addToCurrentlyOpenOverlays,
      removeFromCurrentlyOpenOverlays,
      teleportModalOpen,
      setTeleportModalOpen,
      setIsTeleportingToRoomByUnreal,
    ],
  );

  const handleCloseDrawer = useCallback(() => {
    if (isOpen) {
      handleClose();
      removeFromCurrentlyOpenOverlays(overlayName);
      onClick?.(false);
    }
  }, [handleClose, onClick, isOpen]);

  const handleClickAway = () => {
    if (!disableClickAway && !hideCurrentlyOpenOverlays) {
      handleCloseDrawer();
    }
  };

  useEffect(() => {
    if (hideCurrentlyOpenOverlays) {
      handleClose();
    } else if (isOverlayCurrentlyOpen(overlayName)) {
      handleOpen();
    } else if (isOpen) {
      handleClose();
    }
  }, [hideCurrentlyOpenOverlays, isOverlayCurrentlyOpen, overlayName, isOpen]);

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box>
        <HomeBarButton
          label={label}
          icon={icon}
          isActive={isOpen}
          handleClick={handleToggleDrawer}
          buttonSx={buttonSx}
          disabled={disabled}
          dataTestId={dataTestId}
        />

        <SwipeableDrawer
          position={position}
          open={isOpen}
          onClose={handleCloseDrawer}
          onOpen={handleToggleDrawer}
          sx={popoverSx}
        >
          {children({ onClose: handleCloseDrawer, isOpen })}
        </SwipeableDrawer>
      </Box>
    </ClickAwayListener>
  );
};

export default IconPopover;
